import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import aircraftService from '../../services/aircraftService';

// Debounce utility
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AircraftModelSelect = ({ value, onChange, error, required = false, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const cacheRef = useRef(new Map()); // Cache for search results
  const abortControllerRef = useRef(null);

  // Debounce search term to reduce API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Load initial options on mount
  useEffect(() => {
    loadOptions('', 1);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Load options when debounced search term changes
  useEffect(() => {
    if (isOpen) {
      loadOptions(debouncedSearchTerm, 1);
    }
  }, [debouncedSearchTerm, isOpen]);

  const loadOptions = useCallback(async (search = '', pageNum = 1) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Check cache first
    const cacheKey = `${search}_${pageNum}`;
    if (cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      if (pageNum === 1) {
        setOptions(cached.data);
      } else {
        setOptions(prev => [...prev, ...cached.data]);
      }
      setHasMore(cached.hasMore);
      setPage(pageNum);
      return;
    }

    setLoading(true);
    abortControllerRef.current = new AbortController();

    try {
      const response = await aircraftService.getAircraftModels({ 
        search, 
        page: pageNum 
      }, abortControllerRef.current.signal);
      
      // Handle raw array response from backend (matching PHP format)
      const rawModels = Array.isArray(response.data) ? response.data : [];
      
      // Format the models for display
      const formattedModels = rawModels.map(model => ({
        id: `${model.manaufacturer}/${model.model}/${model.type_designator}`,
        text: `${model.manaufacturer} / ${model.model} / ${model.type_designator}`,
        manaufacturer: model.manaufacturer,
        model: model.model,
        type_designator: model.type_designator,
        model_id: model.id
      }));
      
      // For now, assume there's more data if we got 200 items
      const hasMoreResults = rawModels.length >= 200;

      // Cache the result
      cacheRef.current.set(cacheKey, {
        data: formattedModels,
        hasMore: hasMoreResults
      });

      // Limit cache size to prevent memory issues
      if (cacheRef.current.size > 50) {
        const firstKey = cacheRef.current.keys().next().value;
        cacheRef.current.delete(firstKey);
      }
      
      if (pageNum === 1) {
        setOptions(formattedModels);
      } else {
        setOptions(prev => [...prev, ...formattedModels]);
      }
      
      setHasMore(hasMoreResults);
      setPage(pageNum);
    } catch (error) {
      // Ignore abort/cancel errors - they're expected when debouncing
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.message === 'canceled') {
        return;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSelect = useCallback((option) => {
    onChange(option.id);
    setIsOpen(false);
    setSearchTerm('');
  }, [onChange]);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadOptions(debouncedSearchTerm, page + 1);
    }
  }, [loading, hasMore, debouncedSearchTerm, page, loadOptions]);

  const selectedOption = useMemo(() => 
    options.find(opt => opt.id === value),
    [options, value]
  );

  const displayValue = selectedOption ? selectedOption.text : 'Select Aircraft Model';

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <div
        className={`w-full h-[34px] py-2 px-3 text-[13px] text-[#333] bg-white border ${error ? 'border-red-500' : 'border-[#ddd]'} rounded box-border transition-[border-color] duration-200 ${disabled ? 'cursor-not-allowed bg-[#f5f5f5]' : 'cursor-pointer hover:border-[#ffd200]'} flex items-center justify-between focus:outline-none focus:border-[#ffd200]`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className={value ? 'text-[#333]' : 'text-[#999]'}>{displayValue}</span>
        <svg className="w-3 h-3 text-[#333]" fill="currentColor" viewBox="0 0 12 12">
          <path d="M6 9L1 4h10z" />
        </svg>
      </div>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#ddd] rounded shadow-lg z-50 max-h-[300px] overflow-hidden flex flex-col">
          <div className="p-2 border-b border-[#ddd]">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search aircraft models..."
              className="w-full py-1.5 px-2 text-[13px] border border-[#ddd] rounded focus:outline-none focus:border-[#ffd200]"
            />
          </div>
          
          <div className="overflow-y-auto flex-1">
            {loading && options.length === 0 ? (
              <div className="p-3 text-center text-[13px] text-[#999]">Loading...</div>
            ) : options.length === 0 ? (
              <div className="p-3 text-center text-[13px] text-[#999]">No results found</div>
            ) : (
              <>
                {options.map((option) => (
                  <div
                    key={option.id}
                    className={`py-2 px-3 text-[13px] cursor-pointer transition-colors ${
                      value === option.id ? 'bg-[#ffd200] text-black' : 'hover:bg-[#f5f5f5]'
                    }`}
                    onClick={() => handleSelect(option)}
                  >
                    {option.text}
                  </div>
                ))}
                
                {hasMore && (
                  <div
                    className="py-2 px-3 text-[13px] text-center text-[#666] cursor-pointer hover:bg-[#f5f5f5] border-t border-[#ddd]"
                    onClick={handleLoadMore}
                  >
                    {loading ? 'Loading more...' : 'Load more'}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AircraftModelSelect;
